import { getAllGuestCartItems, clearGuestCart } from "../utils/guestCartDB";
import { postRecord } from "./services";

const API_AddToCart = "cart/create";
const API_UpdateCart = "cart/update";

/**
 * Global Cart Transfer Service
 * Handles transferring guest cart items to authenticated user's cart
 * with proper duplicate detection, error handling, and progress tracking
 */

/**
 * Helper function to check if choice options are empty
 * Handles: null, undefined, "", "{}", "[]", {}, []
 */
const hasChoiceOptions = (choiceOptions) => {
  // Handle null/undefined
  if (!choiceOptions) {
    return false;
  }

  // Handle string format
  if (typeof choiceOptions === "string") {
    const trimmed = choiceOptions.trim();
    if (trimmed === "" || trimmed === "{}" || trimmed === "[]") {
      return false;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return hasChoiceOptions(parsed); // Recursive call with parsed object
    } catch (error) {
      return false;
    }
  }

  // Handle array format
  if (Array.isArray(choiceOptions)) {
    return choiceOptions.length > 0;
  }

  // Handle object format
  if (typeof choiceOptions === "object") {
    return Object.keys(choiceOptions).length > 0;
  }

  return false;
};

/**
 * Check if two cart items are duplicates based on business logic
 * Business Rules:
 * 1. Same Product_Id AND Combination_Id
 * 2. If EITHER item has choice options, they are different items
 * 3. If BOTH items have NO choice options, they are duplicates (combine quantities)
 */
const areItemsDuplicate = (guestItem, authItem) => {
  // Convert Product_Id to strings for comparison (handle number vs string mismatch)
  const guestProductId = String(guestItem.Product_Id || "");
  const authProductId = String(authItem.Product_Id || "");

  if (guestProductId !== authProductId) {
    return false;
  }

  // Convert Combination_Id to strings for comparison (handle number vs string mismatch)
  const guestCombId = String(guestItem.Combination_Id || "");
  const authCombId = String(authItem.Combination_Id || "");

  if (guestCombId !== authCombId) {
    return false;
  }

  // Check choice options
  const guestHasChoices = hasChoiceOptions(guestItem.Choice_Options);
  const authHasChoices = hasChoiceOptions(authItem.Choice_Options);

  // If either has choice options, they are unique items
  if (guestHasChoices || authHasChoices) {
    return false;
  }

  return true;
};
/**
 * Map guest cart item to API payload format
 * @param {Object} guestItem - Guest cart item from IndexedDB
 * @param {number} userId - Current authenticated user ID
 * @returns {Object} - API payload format
 */
const mapGuestItemToApiPayload = (guestItem, userId) => {
  // Choice_Options should be an object, not a string
  const choiceOptions =
    typeof guestItem.Choice_Options === "string"
      ? JSON.parse(guestItem.Choice_Options || "{}")
      : guestItem.Choice_Options || {};

  // Convert Addons to array if it's a string
  const addons =
    typeof guestItem.Addons === "string"
      ? JSON.parse(guestItem.Addons || "[]")
      : guestItem.Addons || [];

  return {
    Product_Id: guestItem.Product_Id,
    Combination_Id: guestItem.Combination_Id || null,
    Units: guestItem.Units || guestItem.Quantity || 1,
    Price_Per_Unit: guestItem.Price_Per_Unit || guestItem.price || 0,
    Total_Price:
      guestItem.Total_Price || guestItem.Units * guestItem.Price_Per_Unit || 0,
    Choice_Options: choiceOptions, // Object, not string
    Addons: addons, // Array, not string
    Is_Savefor_Later: String(
      guestItem.Is_Savefor_Later || guestItem.Save_For_Later || "0"
    ),
    User_Id: userId,
    Added_By: userId,
  };
};

/**
 * Handle API response from cart operations
 * @param {Object} response - API response object
 * @returns {Object} - Processed result
 */
const handleCartApiResponse = (response) => {
  try {
    if (response && response.status === 200 && response.data) {
      // Check if response has the expected structure
      if (response.data.Response_Data) {
        const result = JSON.parse(response.data.Response_Data);
        return {
          success: true,
          data: result,
          message: result.Message || "Operation successful",
        };
      } else if (response.data) {
        // Direct data response
        return {
          success: true,
          data: response.data,
          message: "Operation successful",
        };
      }
    }

    // Handle error responses
    let errorMessage = "Failed to process request";
    if (response) {
      console.log("API Error Response:", response);

      if (response.data && response.data.Response_Data) {
        try {
          const errorData = JSON.parse(response.data.Response_Data);
          errorMessage = errorData.Message || errorMessage;
        } catch (e) {
          // Use default error message
        }
      } else if (response.data && typeof response.data === "string") {
        errorMessage = response.data;
      } else if (response.statusText) {
        errorMessage = response.statusText;
      } else if (response.status) {
        errorMessage = `HTTP ${response.status} Error`;
      }
    }

    return {
      success: false,
      error: errorMessage,
      message: errorMessage,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: "Failed to process API response",
    };
  }
};

/**
 * Transfer a single guest cart item to authenticated cart
 * @param {Object} item - Guest cart item
 * @param {Array} existingCartItems - Current authenticated cart items
 * @param {number} userId - Current authenticated user ID
 * @returns {Promise<Object>} - Transfer result
 */
const transferSingleItem = async (item, existingCartItems, userId) => {
  try {
    // Check for duplicates based on business logic
    const existingItem = existingCartItems.find((authItem) =>
      areItemsDuplicate(item, authItem)
    );

    if (existingItem) {
      // Business Logic: Items without choice options should have quantities combined
      const existingQty = existingItem.Units || existingItem.Quantity || 1;
      const guestQty = item.Units || item.Quantity || 1;
      const newQuantity = existingQty + guestQty;

      // Get price from existing item (authenticated cart price takes precedence)
      const pricePerUnit =
        existingItem.Price_Per_Unit ||
        existingItem.Total_Selling_Price ||
        existingItem.price ||
        item.Price_Per_Unit ||
        item.price ||
        0;

      // Prepare update payload (match ProductCard/ProductView pattern)
      const updatePayload = {
        Cart_Id: existingItem.Cart_Id,
        User_Id: userId,
        Product_Id: existingItem.Product_Id,
        Combination_Id: existingItem.Combination_Id || null,
        Units: newQuantity,
        Price_Per_Unit: pricePerUnit,
        Total_Price: newQuantity * pricePerUnit,
        Is_Savefor_Later: existingItem.Is_Savefor_Later || "0",
        Modified_By: userId,
        Choice_Options: existingItem.Choice_Options || null,
      };

      try {
        const response = await postRecord(API_UpdateCart, updatePayload);
        const result = handleCartApiResponse(response);

        if (result.success) {
          return {
            success: true,
            item: item,
            action: "quantity_updated",
            message: `Quantity updated to ${newQuantity}`,
            data: result.data,
          };
        } else {
          return {
            success: false,
            item: item,
            action: "update_failed",
            message: result.message || "Failed to update quantity",
            error: result.error,
          };
        }
      } catch (apiError) {
        return {
          success: false,
          item: item,
          action: "update_failed",
          message: "Failed to update existing item",
          error: apiError.message,
        };
      }
    } else {
      // Add new item (either has choice options or no duplicate found)
      const payload = mapGuestItemToApiPayload(item, userId);

      try {
        const response = await postRecord(API_AddToCart, payload);
        const result = handleCartApiResponse(response);

        if (result.success) {
          return {
            success: true,
            item: item,
            action: "added",
            message: "Item added successfully",
            data: result.data,
          };
        } else {
          return {
            success: false,
            item: item,
            action: "failed",
            message: result.message || "Failed to add item",
            error: result.error,
          };
        }
      } catch (apiError) {
        return {
          success: false,
          item: item,
          action: "failed",
          message: "API call failed",
          error: apiError.message,
        };
      }
    }
  } catch (error) {
    return {
      success: false,
      item: item,
      action: "failed",
      message: "Network error occurred",
      error: error.message,
    };
  }
};

/**
 * Main cart transfer function
 * @param {Array} existingCartItems - Current authenticated cart items
 * @param {Object} user - Current authenticated user object
 * @param {Function} onProgress - Progress callback (current, total, result)
 * @returns {Promise<Object>} - Overall transfer result
 */
export const transferGuestCartToAuthenticated = async (
  existingCartItems = [],
  user = null,
  onProgress = null
) => {
  try {
    // Get all guest cart items
    const guestCartItems = await getAllGuestCartItems();

    if (!guestCartItems || guestCartItems.length === 0) {
      return {
        success: true,
        totalItems: 0,
        successfulItems: [],
        failedItems: [],
        skippedItems: [],
        message: "No guest cart items to transfer",
      };
    }

    // Check if we have a valid user
    if (!user || !user.User_Id) {
      return {
        success: false,
        totalItems: guestCartItems.length,
        successfulItems: [],
        failedItems: guestCartItems.map((item) => ({
          item,
          error: "User not authenticated",
        })),
        skippedItems: [],
        message: "User authentication required for cart transfer",
      };
    }

    const results = {
      totalItems: guestCartItems.length,
      successfulItems: [],
      failedItems: [],
      skippedItems: [],
    };

    // Process each item
    for (let i = 0; i < guestCartItems.length; i++) {
      const item = guestCartItems[i];

      // Call progress callback
      if (onProgress) {
        onProgress(i + 1, guestCartItems.length, null);
      }

      const transferResult = await transferSingleItem(
        item,
        existingCartItems,
        user.User_Id
      );

      if (transferResult.success) {
        if (transferResult.action === "duplicate_skipped") {
          results.skippedItems.push({
            item: transferResult.item,
            reason: transferResult.message,
          });
        } else {
          results.successfulItems.push({
            item: transferResult.item,
            data: transferResult.data,
          });
        }
      } else {
        results.failedItems.push({
          item: transferResult.item,
          error: transferResult.message,
          details: transferResult.error,
        });
      }

      // Call progress callback with result
      if (onProgress) {
        onProgress(i + 1, guestCartItems.length, transferResult);
      }

      // Small delay to prevent API overwhelm
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Determine overall success
    const hasFailures = results.failedItems.length > 0;
    const hasSuccesses = results.successfulItems.length > 0;

    let message;
    if (!hasFailures && hasSuccesses) {
      message = `Successfully transferred ${results.successfulItems.length} items to your cart`;
    } else if (
      !hasFailures &&
      !hasSuccesses &&
      results.skippedItems.length > 0
    ) {
      message = `All ${results.skippedItems.length} items were already in your cart`;
    } else if (hasFailures && hasSuccesses) {
      message = `Transferred ${results.successfulItems.length} items, ${results.failedItems.length} failed`;
    } else if (hasFailures && !hasSuccesses) {
      message = `Failed to transfer ${results.failedItems.length} items`;
    } else {
      message = "Cart transfer completed";
    }

    return {
      success: !hasFailures || hasSuccesses,
      message,
      ...results,
    };
  } catch (error) {
    return {
      success: false,
      message: "Failed to transfer cart items",
      error: error.message,
      totalItems: 0,
      successfulItems: [],
      failedItems: [],
      skippedItems: [],
    };
  }
};

/**
 * Retry failed items from a previous transfer
 * @param {Array} failedItems - Failed items from previous transfer
 * @param {Array} existingCartItems - Current authenticated cart items
 * @param {Object} user - Current authenticated user object
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} - Retry result
 */
export const retryFailedTransfer = async (
  failedItems,
  existingCartItems = [],
  user = null,
  onProgress = null
) => {
  if (!failedItems || failedItems.length === 0) {
    return {
      success: true,
      totalItems: 0,
      successfulItems: [],
      failedItems: [],
      message: "No failed items to retry",
    };
  }

  const results = {
    totalItems: failedItems.length,
    successfulItems: [],
    failedItems: [],
  };

  for (let i = 0; i < failedItems.length; i++) {
    const failedItemData = failedItems[i];
    const item = failedItemData.item;

    if (onProgress) {
      onProgress(i + 1, failedItems.length, null);
    }

    // Check if we have a valid user before processing
    if (!user || !user.User_Id) {
      results.failedItems.push({
        item: item,
        error: "User not authenticated for retry",
        details: "User object is null or missing User_Id",
      });
      continue;
    }

    const transferResult = await transferSingleItem(
      item,
      existingCartItems,
      user.User_Id
    );

    if (transferResult.success) {
      results.successfulItems.push({
        item: transferResult.item,
        data: transferResult.data,
      });
    } else {
      results.failedItems.push({
        item: transferResult.item,
        error: transferResult.message,
        details: transferResult.error,
      });
    }

    if (onProgress) {
      onProgress(i + 1, failedItems.length, transferResult);
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  const hasFailures = results.failedItems.length > 0;
  const hasSuccesses = results.successfulItems.length > 0;

  let message;
  if (!hasFailures && hasSuccesses) {
    message = `Successfully transferred ${results.successfulItems.length} items on retry`;
  } else if (hasFailures && hasSuccesses) {
    message = `Retry: ${results.successfulItems.length} succeeded, ${results.failedItems.length} still failed`;
  } else if (hasFailures && !hasSuccesses) {
    message = `Retry failed: ${results.failedItems.length} items could not be transferred`;
  } else {
    message = "Retry completed";
  }

  return {
    success: !hasFailures || hasSuccesses,
    message,
    ...results,
  };
};

/**
 * Clear guest cart after successful transfer
 * @returns {Promise<boolean>} - Success status
 */
export const clearGuestCartAfterTransfer = async () => {
  try {
    await clearGuestCart();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get product names from items for user-friendly error messages
 * @param {Array} items - Array of cart items
 * @returns {Array} - Array of product names
 */
export const getProductNamesFromItems = (items) => {
  return items.map((itemData) => {
    const item = itemData.item || itemData;
    return (
      item.Product_Name ||
      item.Product_Title ||
      `Product ID: ${item.Product_Id}`
    );
  });
};

export default {
  transferGuestCartToAuthenticated,
  retryFailedTransfer,
  clearGuestCartAfterTransfer,
  getProductNamesFromItems,
};
